import { bookingCaptainStatus } from 'stacks/messaging/helpers';
import styled from 'styled-components';
import useModal from 'components/remodal/v2/useModal';
import Avatar from 'components/ui/Avatar/Avatar';
import StarRating from 'components/ui/StarRating/StarRating';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import Button from 'components/ui/Button/Button';
import BookingBadge from 'components/ui/BookingBadge/BookingBadge';
import ChangeCaptainModal from 'components/pdp/v3/modals/ChangeCaptainModal';
import { useAssignCaptainMutation } from 'components/booking/mutationHooks';
import { useSetAtom } from 'jotai';
import { bookingCaptainIdAtom } from 'components/forms/bookingWidget/jotaiStore';
import { useQueryClient } from 'react-query';
import LoadingSpinner from 'components/loading/LoadingSpinner';
import { useEffect } from 'react';
import { getProfilePicFromUser } from 'utils/images';
import { valueEmpty } from '../../../helpers';

const ownerResponseLabel = (rate) => {
  let label = ['#f4c064', '#17233CFF'];
  if (rate >= 85) {
    label = ['#2a8401', '#ffffff'];
  }
  if (rate >= 70 && rate <= 84) {
    label = ['#f4c064', '#17233CFF'];
  }
  if (rate < 70) {
    label = ['#BC350AFF', '#ffffff'];
  }
  return label;
};

const MessageCaptainOwnerInfo = (props) => {
  const { userType, captain, owner, captainState, boatId, bookingId, isLoadingConversation } = props;

  const captainMissing = {
    id: '',
    first_name: '',
    last_name: '',
    picture: {
      url: 'default_profile_pic.png',
    },
    profile_text: '',
    trips_completed: 0,
    rating: null,
    review_count: null,
  };

  const textColor = (() => {
    if (userType === 'owner') return '#0e7873';
    if (userType === 'captain') return '#00acb7';
    if (userType === 'renter') return '#0751c2';
    return 'black';
  })();

  const captainUser = !valueEmpty(captain) ? captain : captainMissing;

  const userInfo = userType === 'captain' ? owner : captainUser;

  const { first_name, id: userId, rating, review_count } = userInfo;

  const profilePic = getProfilePicFromUser(userInfo);

  const ownerResponseRate = owner && owner.response_rate ? parseFloat(owner.response_rate) : 0;

  const viewProfile = () => {
    window.open(`/users/${userId}`, '_blank');
  };

  const setBookingCaptainId = useSetAtom(bookingCaptainIdAtom);
  useEffect(() => {
    setBookingCaptainId(captain?.id);
  }, [captain?.id, setBookingCaptainId]);

  const queryClient = useQueryClient();
  const { mutate: assignCaptainMutate, isLoading: isAssigningCaptain } = useAssignCaptainMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversationDetail'] });
    },
  });
  const onSelectCaptain = (captainId) => {
    assignCaptainMutate({ bookingId, body: { captain_id: captainId } });
    closeCaptainModal();
  };

  const {
    modal: assignCaptainModal,
    openModal: openAssignCaptainModal,
    closeModal: closeCaptainModal,
  } = useModal(<ChangeCaptainModal boatId={boatId} onSelectCaptain={onSelectCaptain} />, {
    isOnRight: true,
    notScrollAble: true,
    trackingName: 'change_captain_modal',
  });

  const infoTitle = (type) => {
    switch (type) {
      case 'captain':
        return 'Boat owner';
      case 'renter':
        return 'Your captain';
      case 'owner':
        return 'Captain';
      default:
        return 'Captain';
    }
  };

  return isAssigningCaptain || isLoadingConversation ? (
    <CaptainSectionWrapper>
      <LoadingSpinner />
    </CaptainSectionWrapper>
  ) : (
    <div>
      <Section>
        <DoubleLeft>{infoTitle(userType)}</DoubleLeft>
        <Right>
          <ProfileInfo>
            <SmallBold>{`${first_name}`}</SmallBold>
            {userInfo.id !== '' && (
              <StarRating
                rating={rating > 0 ? parseFloat(rating).toPrecision(2) : null}
                count={review_count > 0 ? review_count : null}
              />
            )}
          </ProfileInfo>
          <div>
            <Avatar diameter={48} profileLink={`/users/${userId}`} src={profilePic} />
          </div>
        </Right>
      </Section>

      {userType !== 'renter' && userInfo.id !== '' && (
        <ViewProfileWrapper>
          <Button
            text="View profile"
            onClick={viewProfile}
            outline
            variant={userType === 'captain' ? 'darkCaptain' : userType}
          />
        </ViewProfileWrapper>
      )}

      {userType === 'captain' ? (
        <div>
          <Section>
            <Left>Avg. response time</Left>
            <Right>{owner.response_time}</Right>
          </Section>
          <Section>
            <Left>Response rate</Left>
            <Right>
              <Label $color={ownerResponseLabel(ownerResponseRate)}>{owner.response_rate}</Label>
            </Right>
          </Section>
        </div>
      ) : (
        <div>
          <Section>
            <Left>Status</Left>
            <Right>
              <BookingBadge bookingStatus={bookingCaptainStatus(captainState || 'pending')} />
              {(userType === 'renter' || userType === 'owner') && userInfo.id === '' && (
                <Tooltip trigger="mouseenter" place="top" triggerWith={<HelpIcon />}>
                  <SmallTooltip style={{ maxWidth: '175px' }}>
                    The captain is pending to be assigned to this trip.{' '}
                  </SmallTooltip>
                </Tooltip>
              )}
            </Right>
          </Section>

          {userInfo.id !== '' && (
            <div>
              <Section>
                <Left>Trips completed</Left>
                <Right>{userInfo.trips_completed}</Right>
              </Section>

              <Section>
                <Left>U.S. Coast Guard verified</Left>
                <Right>
                  <img
                    alt="U.S. Coast Guard Verified"
                    src="/images/icons/verified-full.svg"
                    style={{ width: 24, height: 24 }}
                  />
                </Right>
              </Section>
            </div>
          )}

          {captainState !== 'approved' && (
            <AssignCaptainWrapper>
              <a style={{ color: textColor }} onClick={openAssignCaptainModal} href="#">
                {userInfo.id !== '' ? 'Change captain' : 'Assign a captain'}
              </a>
            </AssignCaptainWrapper>
          )}
        </div>
      )}

      {assignCaptainModal}
    </div>
  );
};

const CaptainSectionWrapper = styled.div`
  min-height: 266px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Label = styled.span<{ $color: string[] }>`
  font-weight: bold;
  border-radius: 3px;
  padding: 5px 8px;
  font-size: 10px;
  text-align: center;
  color: ${({ $color }) => $color[1]};
  background: ${({ $color }) => $color[0]};
`;

const SmallTooltip = styled.div`
  font-size: 13px;
`;

const HelpSvg = styled.svg`
  width: 20px;
  height: 20px;
  margin-left: 5px;
`;

const HelpIcon = () => (
  <HelpSvg xmlns="http://www.w3.org/2000/svg" fill="#0a4195" viewBox="0 0 1008 1008">
    <path d="M504 980.2C239.9 980.2 27.8 768.1 27.8 504S239.9 27.8 504 27.8 980.2 239.9 980.2 504 768.1 980.2 504 980.2zm0-887.4C274.6 92.8 92.8 274.6 92.8 504S274.6 915.2 504 915.2 915.2 733.4 915.2 504 733.4 92.8 504 92.8zm34.6 467.5v30.3c0 17.3-13 34.6-34.6 34.6s-34.6-13-34.6-34.6v-51.9c0-13 4.3-21.6 13-26h4.3l34.6-17.3h8.7c4.3 0 8.7-4.3 8.7-4.3 43.3-17.3 56.3-34.6 56.3-73.6 0-51.9-43.3-90.9-95.2-90.9-43.3 0-82.2 30.3-95.2 69.3 0 8.7-17.3 21.6-34.6 17.3-17.3-8.7-26-26-21.6-43.3C365.7 300.6 430.6 253 504.2 253c90.9 0 160.2 69.3 160.2 155.8 0 69.3-30.3 108.2-95.2 134.2-4.3 0-8.7 4.3-13 4.3h-8.6c-4.6 8.6-9 8.6-9 13zM504 755.1c-21.6 0-39-17.3-39-39s17.3-39 39-39 39 17.3 39 39-17.4 39-39 39z" />
  </HelpSvg>
);

const AssignCaptainWrapper = styled.div`
  padding-top: 10px;
  font-size: 15px;
`;

const ViewProfileWrapper = styled.div`
  margin-bottom: 25px;
  button {
    width: 100%;
  }
`;

const Section = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 10px;
`;

const ProfileInfo = styled.div`
  margin-right: 5px;
`;

const Left = styled.div`
  font-size: 15px;
  //width: 25%;
`;

const DoubleLeft = styled(Left)`
  width: 25%;
`;

const SmallBold = styled.div`
  font-weight: 600;
  font-size: 14px;
  text-align: right;
  margin-right: 4px;
`;

const Right = styled.div`
  display: flex;
  margin-left: auto;
  font-weight: 600;
`;

export default MessageCaptainOwnerInfo;
